/* eslint-disable react/forbid-component-props */
import { Stack, SxProps, Typography, useMediaQuery } from '@mui/material';
import {
	BenefitsGrayContentSX,
	BenefitsGrayDescriptionSX,
	BenefitsGrayFrameSX,
	BenefitsGrayItemSX,
	BenefitsGraySlideInnerSX,
	BenefitsGrayTitleSX,
} from '@/components/blocks/BenefitsGray/styles/main';
import { combineSX } from '@/utils/combineSX';
import { useTheme } from '@mui/material/styles';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/autoplay';
import { DpBottomContainer } from '@/components/blocks/DpBottomContainer';
import {
	DpBenefitsProvider,
	useDpBenefits,
	useDpBenefitsContext,
} from '@/components/blocks/DpBenefitsContext';
import { parseHTML } from '@/utils/parseHTML';
import React, { Fragment } from 'react';
import { DpBenefitsDrawer } from '@/components/blocks/Benefits';
import { Img } from '@/components/blocks/MaterialImage';

const ITEMS = [
	{
		title: '30 días para cambios y devoluciones',
		description: 'sin complicaciones',
		icon: 'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/diseno/icons/bg10days.png',
		spot: 'DrawerReturnsExchangesInformative',
	},
	{
		title: 'Envío gratis a todo méxico',
		description: 'en compras +$999',
		icon: 'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/diseno/icons/bgshipping.png',
		spot: 'DrawerDeliveriesShipmentsInformative',
	},
	{
		title: 'Compra en línea y paga en efectivo',
		description: 'en OXXO y BBVA',
		icon: 'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/diseno/icons/bgpaymentmoney.png',
		spot: 'DrawerPaymentMethodsInformative',
	},
	{
		title: 'Elige el método de pago que prefieras',
		description: 'pagos sin preocupaciones',
		icon: 'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/diseno/icons/bgpaymetcard.png',
		spot: 'DrawerHowToBuy',
	},
];

export const DpBenefitGray: React.FC<{
	title: string;
	description: string;
	spot: string;
	src: string;
}> = ({ title, description, spot, src }) => {
	const { onOpenDrawer } = useDpBenefitsContext();

	return (
		<Stack
			sx={BenefitsGrayItemSX}
			onClick={() =>
				onOpenDrawer({
					title,
					emsName: spot,
				})
			}
		>
			<Img src={src} alt={title} sx={{ width: '32px', objectFit: 'cover' }} />
			<Typography sx={BenefitsGrayTitleSX}>{parseHTML(title)}</Typography>
			<Typography sx={BenefitsGrayDescriptionSX}>{parseHTML(description)}</Typography>
		</Stack>
	);
};

interface DpBenefitsGrayFrameProps {
	framesx?: SxProps;
	sx?: SxProps;
	children?: React.ReactNode;
}

export const DpBenefitsGrayFrame: React.FC<DpBenefitsGrayFrameProps> = ({
	framesx,
	sx,
	children,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const cleanChildren = React.Children.toArray(children).filter((child) =>
		React.isValidElement(child)
	);

	return (
		<DpBottomContainer
			benefitGray
			framesx={combineSX([BenefitsGrayFrameSX, ...(Array.isArray(framesx) ? framesx : [framesx])])}
			sx={combineSX([BenefitsGrayContentSX, ...(Array.isArray(sx) ? sx : [sx])])}
		>
			{isMobile ? (
				<Swiper
					modules={[Autoplay]}
					autoplay={{ delay: 8000 }}
					loop
					slidesPerView={1}
					style={{ width: '100%' }}
				>
					{cleanChildren.length > 0 ? (
						<Fragment>
							{cleanChildren.map((item, index) => (
								<SwiperSlide key={`id_swiper_${index}`}>
									<Stack sx={BenefitsGraySlideInnerSX} justifyContent="center" alignItems="center">
										{item}
									</Stack>
								</SwiperSlide>
							))}
						</Fragment>
					) : (
						<Fragment>
							{ITEMS.map((item, index) => (
								<SwiperSlide key={`id_swiper_${index}`}>
									<Stack sx={BenefitsGraySlideInnerSX} justifyContent="center" alignItems="center">
										<DpBenefitGray
											key={item.title}
											title={item.title}
											description={item.description}
											spot={item.spot}
											src={item.icon}
										/>
									</Stack>
								</SwiperSlide>
							))}
						</Fragment>
					)}
				</Swiper>
			) : (
				<>
					{cleanChildren.length > 0 ? (
						<Fragment>{cleanChildren}</Fragment>
					) : (
						<Fragment>
							{ITEMS.map((item) => (
								<DpBenefitGray
									key={item.title}
									title={item.title}
									description={item.description}
									spot={item.spot}
									src={item.icon}
								/>
							))}
						</Fragment>
					)}
				</>
			)}
		</DpBottomContainer>
	);
};

export const BenefitsGray: React.FC<DpBenefitsGrayFrameProps> = (props) => {
	const dpBenefits = useDpBenefits();

	return (
		<DpBenefitsProvider value={dpBenefits}>
			<DpBenefitsGrayFrame {...props} />
			<DpBenefitsDrawer />
		</DpBenefitsProvider>
	);
};
